// useKeyboardAwareFocus.ts
import { useEffect } from "react";

type Opts = {
    /** 요소가 이 높이 이상 보이면 "보임"으로 간주 (기본 24px) */
    minVisiblePx?: number;
    /** 가시영역 벗어나면 blur (기본 true) */
    autoBlurIfOutOfView?: boolean;
    /** 스크롤 대상 컨테이너(없으면 window) */
    scrollContainer?: HTMLElement | null;

    /** 최초 포커스 직후 blur 금지 시간 (기본 500ms) */
    initialGraceMs?: number;

    /** 스크롤 중에도 지속 체크 여부 (기본 true) */
    checkDuringScroll?: boolean;
    /** raf 루프 샘플링 최소 간격(ms) (기본 50ms = 최대 20fps) */
    duringScrollSampleMs?: number;
    /** 스크롤이 멈췄다고 간주할 휴지시간(ms) (기본 120ms) */
    scrollIdleMs?: number;
};

export function useKeyboardAwareFocus(opts: Opts = {}) {
    const {
        minVisiblePx = 24,
        autoBlurIfOutOfView = true,
        scrollContainer = null,
        initialGraceMs = 500,

        checkDuringScroll = true,
        duringScrollSampleMs = 50,
        scrollIdleMs = 120,
    } = opts;

    useEffect(() => {
        const vv = (window as any).visualViewport as VisualViewport | undefined;

        const getViewportRect = () => {
            if (vv) return { top: vv.offsetTop, left: vv.offsetLeft, width: vv.width, height: vv.height };
            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
        };

        const isEditable = (el: Element | null): el is HTMLElement => {
            if (!el) return false;
            const t = (el as HTMLElement).tagName;
            return t === "INPUT" || t === "TEXTAREA" || (el as HTMLElement).isContentEditable === true;
        };

        const activeEditable = (): HTMLElement | null => {
            const ae = document.activeElement as HTMLElement | null;
            return isEditable(ae) ? ae : null;
        };

        // 기존 기준: “보이는 높이 >= minVisiblePx”
        const isVisibleEnough = (el: HTMLElement): boolean => {
            const rect = el.getBoundingClientRect();
            const vp = getViewportRect();
            const visibleTop = Math.max(rect.top, vp.top);
            const visibleBottom = Math.min(rect.bottom, vp.top + vp.height);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            return visibleHeight >= minVisiblePx;
        };

        let lastFocusTs = 0;

        // ----- 스크롤 중 실시간 체크용 상태 -----
        let rafId: number | null = null;
        let lastScrollAt = 0;
        let lastSampleAt = 0;
        const isRafRunning = () => rafId != null;

        const stopRaf = () => {
            if (rafId != null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };

        const startRafIfNeeded = () => {
            if (!checkDuringScroll || isRafRunning()) return;
            const loop = () => {
                rafId = requestAnimationFrame(loop);

                // 스크롤이 idle이면 루프 종료
                if (Date.now() - lastScrollAt > scrollIdleMs) {
                    stopRaf();
                    return;
                }

                // 샘플링 간격 제한(성능)
                if (Date.now() - lastSampleAt < duringScrollSampleMs) return;
                lastSampleAt = Date.now();

                // 초기 포커스 유예 중이면 blur 금지
                if (Date.now() - lastFocusTs < initialGraceMs) return;

                // 스크롤 중에는 "안정화 대기" 없이 바로 판정
                if (!autoBlurIfOutOfView) return;
                const el = activeEditable();
                if (!el) return;

                if (!isVisibleEnough(el)) {
                    // 즉시 blur(스크롤 중에도 24px 이탈이면 바로 내려감)
                    try { el.blur(); } catch { }
                }
            };
            rafId = requestAnimationFrame(loop);
        };

        const onScroll = () => {
            lastScrollAt = Date.now();
            startRafIfNeeded();
        };

        const onResize = () => {
            // resize 중에도 즉시 체크가 필요하면 scroll과 동일하게 처리
            lastScrollAt = Date.now();
            startRafIfNeeded();
        };

        // 최초 포커스 시 유예 타이머 시작
        const onFocusIn = () => {
            lastFocusTs = Date.now();
        };

        const container = scrollContainer;
        if (container) container.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onResize);
        document.addEventListener("focusin", onFocusIn, true);
        vv?.addEventListener("resize", onResize);
        vv?.addEventListener("scroll", onScroll);

        return () => {
            stopRaf();
            if (container) container.removeEventListener("scroll", onScroll);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("focusin", onFocusIn, true);
            vv?.removeEventListener("resize", onResize);
            vv?.removeEventListener("scroll", onScroll);
        };
    }, [
        minVisiblePx,
        autoBlurIfOutOfView,
        scrollContainer,
        initialGraceMs,
        checkDuringScroll,
        duringScrollSampleMs,
        scrollIdleMs,
    ]);
}
